/**
 * VSCreatureDisplay Component
 * Displays a single "hero" creature in the VS screen with sprite and name.
 *
 * Features:
 * - 96x96 pixel-art scaled sprite (from 64x64 source)
 * - Graceful fallback for missing sprites (shows first letter)
 * - Left/right side styling for VS screen positioning
 */

import { useState } from 'react';
import styles from './VSCreatureDisplay.module.css';

export interface VSCreatureDisplayProps {
  creature: {
    id: string;
    name: string;
    sprite: string; // URL to 64x64 sprite image
  };
  side: 'left' | 'right'; // Which side of VS screen (for styling/alignment)
}

/**
 * VSCreatureDisplay Component
 *
 * Shows a creature's sprite and name for the VS screen battle intro.
 */
export function VSCreatureDisplay({ creature, side }: VSCreatureDisplayProps): JSX.Element {
  const [spriteError, setSpriteError] = useState(false);

  // Get first letter for fallback placeholder
  const initial = creature.name.charAt(0).toUpperCase();

  // Determine if we should show the fallback
  const showFallback = !creature.sprite || spriteError;

  // Truncate name if too long (for display purposes)
  const displayName = creature.name.length > 16
    ? `${creature.name.substring(0, 16)}...`
    : creature.name;

  return (
    <div
      className={`${styles.container} ${styles[side]}`}
      title={creature.name}
    >
      <div className={styles.spriteContainer}>
        {showFallback ? (
          <div className={styles.fallback} aria-label={`${creature.name} placeholder`}>
            <span className={styles.initial}>{initial}</span>
          </div>
        ) : (
          <img
            src={creature.sprite}
            alt={creature.name}
            className={styles.sprite}
            onError={() => setSpriteError(true)}
          />
        )}
      </div>
      <span className={styles.name}>{displayName}</span>
    </div>
  );
}

export default VSCreatureDisplay;
